import React, { useState, useEffect } from 'react';
import './MobileRegistration.css';
import { Eye, EyeOff, X, Upload, Camera, CheckCircle2, AlertCircle } from 'lucide-react';
import { format } from "date-fns";
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import AvatarEditor from 'react-avatar-edit';
import countriesData from '../../Data/countries.json';

interface FormData {
    ProfilePicture?: File | null;
    ProfilePicturePreview?: string | null;
    FirstName: string;
    Email: string;
    RegisteredBy: string;
    Gender: string;
    DateOfBirth: string;
    Country: string;
    Religion: string;
    Password: string;
    ConfirmPassword: string;
}

interface MobileRegistrationProps {
    onSubmit: (data: FormData) => void;
    onClose: () => void;
    loading: boolean;
}

const MobileRegistration: React.FC<MobileRegistrationProps> = ({ onSubmit, onClose, loading }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 9;
    const [formData, setFormData] = useState<FormData>({
        ProfilePicture: null,
        ProfilePicturePreview: null,
        FirstName: '',
        Email: '',
        RegisteredBy: '',
        Gender: '',
        DateOfBirth: '',
        Country: '',
        Religion: '',
        Password: '',
        ConfirmPassword: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [dob, setDob] = useState<Date | null>(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImage, setCroppedImage] = useState<string | null>(null);
    const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
    const [showCustomCountry, setShowCustomCountry] = useState(false);
    const [customCountry, setCustomCountry] = useState('');
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const progress = (currentStep / totalSteps) * 100;

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        switch (step) {
            case 1:
                if (!formData.FirstName.trim()) {
                    newErrors.FirstName = 'First name is required';
                } else if (formData.FirstName.length < 1 || formData.FirstName.length > 22) {
                    newErrors.FirstName = 'This field must contain between 1 and 22 characters.';
                }
                break;
            case 2:
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!formData.Email.trim()) {
                    newErrors.Email = 'Email is required';
                } else if (!emailRegex.test(formData.Email)) {
                    newErrors.Email = 'Please enter a valid email address';
                }
                break;
            case 3:
                if (!formData.RegisteredBy) {
                    newErrors.RegisteredBy = 'Please select who is registering';
                }
                break;
            case 4:
                if (!formData.Gender) {
                    newErrors.Gender = 'Please select your gender';
                }
                break;
            case 5:
                if (!formData.DateOfBirth) {
                    newErrors.DateOfBirth = 'Date of birth is required';
                }
                break;
            case 6:
                if (!formData.Country.trim()) {
                    newErrors.Country = 'Country is required';
                }
                break;
            case 7:
                if (!formData.Religion) {
                    newErrors.Religion = 'Please select your religion';
                }
                break;
            case 8:
                break;
            case 9:
                const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                if (!formData.Password) {
                    newErrors.Password = 'Password is required';
                } else if (formData.Password.length < 8) {
                    newErrors.Password = 'Password must be at least 8 characters long';
                } else if (!passwordRegex.test(formData.Password)) {
                    newErrors.Password = 'Password must contain uppercase, lowercase, number, and special character';
                }
                if (!formData.ConfirmPassword) {
                    newErrors.ConfirmPassword = 'Please confirm your password';
                } else if (formData.ConfirmPassword !== formData.Password) {
                    newErrors.ConfirmPassword = 'Passwords do not match';
                }
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            if (currentStep === totalSteps) {
                onSubmit(formData);
            } else {
                setSlideDirection('left');
                setCurrentStep(currentStep + 1);
            }
        }
    };

    const handleClose = () => {
        if (currentStep > 1) {
            const confirmed = window.confirm('Are you sure you want to exit? Your progress will be lost.');
            if (confirmed) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const validateFile = (file: File): string | null => {
        const maxSize = 5 * 1024 * 1024;
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

        if (!allowedTypes.includes(file.type)) {
            return 'Please upload a valid image file (JPG, PNG, or WebP)';
        }
        if (file.size > maxSize) {
            return 'File size must be less than 5MB';
        }
        return null;
    };

    const resizeImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Could not get canvas context'));
                        return;
                    }

                    const maxDimension = 1200;
                    let width = img.width;
                    let height = img.height;

                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            height = (height / width) * maxDimension;
                            width = maxDimension;
                        } else {
                            width = (width / height) * maxDimension;
                            height = maxDimension;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL(file.type));
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const error = validateFile(file);

            if (error) {
                setUploadError(error);
                setTimeout(() => setUploadError(null), 3000);
                return;
            }

            setUploadError(null);
            setIsUploading(true);

            try {
                const resizedImageUrl = await resizeImage(file);
                setSelectedImageSrc(resizedImageUrl);
                setTimeout(() => {
                    setIsUploading(false);
                    setShowCropModal(true);
                }, 300);
            } catch (error) {
                setIsUploading(false);
                setUploadError('Failed to process image');
                setTimeout(() => setUploadError(null), 3000);
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            const file = files[0];
            const error = validateFile(file);

            if (error) {
                setUploadError(error);
                setTimeout(() => setUploadError(null), 3000);
                return;
            }

            setUploadError(null);
            setIsUploading(true);

            try {
                const resizedImageUrl = await resizeImage(file);
                setSelectedImageSrc(resizedImageUrl);
                setTimeout(() => {
                    setIsUploading(false);
                    setShowCropModal(true);
                }, 300);
            } catch (error) {
                setIsUploading(false);
                setUploadError('Failed to process image');
                setTimeout(() => setUploadError(null), 3000);
            }
        }
    };


    const onCrop = (preview: string) => {
        setCroppedImage(preview);
    };

    const onCloseCrop = () => {
        setCroppedImage(null);
    };

    const onBeforeFileLoad = (elem: React.ChangeEvent<HTMLInputElement>) => {
        const file = elem.target.files?.[0];
        if (file && file.size > 2 * 1024 * 1024) {
            alert("File is too big! Max size is 2MB.");
            elem.target.value = "";
        }
    };

    const handleCropSave = () => {
        if (croppedImage) {
            fetch(croppedImage)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
                    setFormData({ ...formData, ProfilePicture: file, ProfilePicturePreview: croppedImage });
                    if (selectedImageSrc) URL.revokeObjectURL(selectedImageSrc);
                    setSelectedImageSrc(null);
                    setShowCropModal(false);
                });
        }
    };

    const handleCropCancel = () => {
        if (selectedImageSrc) URL.revokeObjectURL(selectedImageSrc);
        setSelectedImageSrc(null);
        setShowCropModal(false);
    };

    useEffect(() => {
        if (dob) {
            setFormData({ ...formData, DateOfBirth: format(dob, "yyyy-MM-dd") });
        }
    }, [dob]);

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">What's your first name?</h2>
                        <input
                            type="text"
                            className="mobile-input"
                            placeholder="First name"
                            value={formData.FirstName}
                            onChange={(e) => setFormData({ ...formData, FirstName: e.target.value })}
                            maxLength={22}
                            autoFocus
                        />
                        {errors.FirstName && <p className="mobile-error">{errors.FirstName}</p>}
                    </div>
                );

            case 2:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">What's your email?</h2>
                        <input
                            type="email"
                            className="mobile-input"
                            placeholder="Email address"
                            value={formData.Email}
                            onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
                            autoFocus
                        />
                        {errors.Email && <p className="mobile-error">{errors.Email}</p>}
                    </div>
                );

            case 3:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">Who is registering?</h2>
                        <div className="mobile-button-group">
                            <button
                                className={`mobile-option-btn ${formData.RegisteredBy === 'Self' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, RegisteredBy: 'Self' })}
                            >
                                Self
                            </button>
                            <button
                                className={`mobile-option-btn ${formData.RegisteredBy === 'Guardian' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, RegisteredBy: 'Guardian' })}
                            >
                                Guardian
                            </button>
                        </div>
                        {errors.RegisteredBy && <p className="mobile-error">{errors.RegisteredBy}</p>}
                    </div>
                );

            case 4:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">What's your gender?</h2>
                        <div className="mobile-button-group">
                            <button
                                className={`mobile-option-btn ${formData.Gender === 'Male' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Gender: 'Male' })}
                            >
                                Male
                            </button>
                            <button
                                className={`mobile-option-btn ${formData.Gender === 'Female' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Gender: 'Female' })}
                            >
                                Female
                            </button>
                            <button
                                className={`mobile-option-btn ${formData.Gender === 'Other' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Gender: 'Other' })}
                            >
                                Other
                            </button>
                        </div>
                        {errors.Gender && <p className="mobile-error">{errors.Gender}</p>}
                    </div>
                );

            case 5:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">When's your birthday?</h2>
                        <input
                            type="text"
                            className="mobile-input"
                            placeholder="yyyy-MM-dd"
                            value={dob ? format(dob, 'yyyy-MM-dd') : ''}
                            readOnly
                            onClick={() => setShowCalendar(true)}
                        />
                        {showCalendar && (
                            <div className="mobile-calendar-overlay" onClick={() => setShowCalendar(false)}>
                                <div className="mobile-calendar-container" onClick={(e) => e.stopPropagation()}>
                                    <DayPicker
                                        mode="single"
                                        selected={dob as Date}
                                        onSelect={(date) => {
                                            setDob(date ?? null);
                                            setShowCalendar(false);
                                        }}
                                        captionLayout="dropdown"
                                        fromYear={1900}
                                        toYear={new Date().getFullYear()}
                                    />
                                </div>
                            </div>
                        )}
                        {errors.DateOfBirth && <p className="mobile-error">{errors.DateOfBirth}</p>}
                    </div>
                );

            case 6:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">Where are you from?</h2>
                        {!showCustomCountry ? (
                            <select
                                className="mobile-input mobile-select"
                                value={formData.Country}
                                onChange={(e) => {
                                    if (e.target.value === 'Others') {
                                        setShowCustomCountry(true);
                                        setFormData({ ...formData, Country: '' });
                                    } else {
                                        setFormData({ ...formData, Country: e.target.value });
                                    }
                                }}
                            >
                                <option value="">Select country</option>
                                {countriesData.map((country) => (
                                    <option key={country.name} value={country.name}>
                                        {country.name}
                                    </option>
                                ))}
                                <option value="Others">Others</option>
                            </select>
                        ) : (
                            <input
                                type="text"
                                className="mobile-input"
                                placeholder="Enter your country"
                                value={customCountry}
                                onChange={(e) => {
                                    setCustomCountry(e.target.value);
                                    setFormData({ ...formData, Country: e.target.value });
                                }}
                                autoFocus
                            />
                        )}
                        {errors.Country && <p className="mobile-error">{errors.Country}</p>}
                    </div>
                );

            case 7:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">What's your religion?</h2>
                        <div className="mobile-button-group">
                            <button
                                className={`mobile-option-btn ${formData.Religion === 'Christian' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Religion: 'Christian' })}
                            >
                                Christian
                            </button>
                            <button
                                className={`mobile-option-btn ${formData.Religion === 'Muslim' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Religion: 'Muslim' })}
                            >
                                Muslim
                            </button>
                            <button
                                className={`mobile-option-btn ${formData.Religion === 'Hindu' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Religion: 'Hindu' })}
                            >
                                Hindu
                            </button>
                            <button
                                className={`mobile-option-btn ${formData.Religion === 'Other' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, Religion: 'Other' })}
                            >
                                Other
                            </button>
                        </div>
                        {errors.Religion && <p className="mobile-error">{errors.Religion}</p>}
                    </div>
                );

            case 8:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">Add your photo</h2>
                        <div className="mobile-photo-upload">
                            <div
                                className={`mobile-upload-wrapper ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    id="mobileProfilePicture"
                                    ref={fileInputRef}
                                    className="mobile-profile-input"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="mobileProfilePicture" className="mobile-upload-label">
                                    <div className={`mobile-upload-circle ${formData.ProfilePicturePreview ? 'has-image' : ''}`}>
                                        {isUploading ? (
                                            <div className="mobile-upload-spinner">
                                                <div className="mobile-spinner"></div>
                                            </div>
                                        ) : formData.ProfilePicturePreview ? (
                                            <>
                                                <img src={formData.ProfilePicturePreview} alt="Profile" className="mobile-profile-preview" />
                                                <div className="mobile-image-overlay">
                                                    <Camera size={28} className="mobile-change-icon" />
                                                    <span className="mobile-change-text">Change</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="mobile-upload-content">
                                                <Camera size={44} className="mobile-upload-camera-icon" />
                                                <span className="mobile-upload-text-main">Add Photo</span>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </div>
                            {uploadError && (
                                <div className="mobile-upload-error">
                                    <AlertCircle size={16} />
                                    <span>{uploadError}</span>
                                </div>
                            )}
                            {formData.ProfilePicturePreview && !uploadError && (
                                <div className="mobile-upload-success">
                                    <CheckCircle2 size={16} />
                                    <span>Photo added</span>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 9:
                return (
                    <div className="mobile-step">
                        <h2 className="mobile-step-title">Create your password</h2>
                        <div className="mobile-password-wrapper">
                            <input
                                type={passwordVisible ? "text" : "password"}
                                className="mobile-input"
                                placeholder="Password"
                                value={formData.Password}
                                onChange={(e) => setFormData({ ...formData, Password: e.target.value })}
                                autoFocus
                            />
                            <button
                                type="button"
                                className="mobile-toggle-password"
                                onClick={() => setPasswordVisible(!passwordVisible)}
                            >
                                {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <div className="mobile-password-wrapper" style={{ marginTop: '1rem' }}>
                            <input
                                type={confirmPasswordVisible ? "text" : "password"}
                                className="mobile-input"
                                placeholder="Confirm password"
                                value={formData.ConfirmPassword}
                                onChange={(e) => setFormData({ ...formData, ConfirmPassword: e.target.value })}
                            />
                            <button
                                type="button"
                                className="mobile-toggle-password"
                                onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                            >
                                {confirmPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <p className="mobile-hint">Must be 8+ characters with uppercase, lowercase, number, and special character</p>
                        {errors.Password && <p className="mobile-error">{errors.Password}</p>}
                        {errors.ConfirmPassword && <p className="mobile-error">{errors.ConfirmPassword}</p>}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="mobile-registration-container">
            <div className="mobile-progress-bar" style={{ width: `${progress}%` }}></div>

            <button className="mobile-close-btn" onClick={handleClose}>
                <X size={24} />
            </button>

            <div className={`mobile-step-container slide-${slideDirection}`} key={currentStep}>
                {renderStep()}
            </div>

            <button
                className={`mobile-next-btn ${loading ? 'loading' : ''}`}
                onClick={handleNext}
                disabled={loading}
            >
                {loading ? 'Processing...' : currentStep === totalSteps ? 'Create Account' : 'Next'}
            </button>

            {showCropModal && (
                <div className="mobile-crop-overlay">
                    <div className="mobile-crop-modal">
                        <div className="mobile-crop-header">
                            <h3>Crop Your Photo</h3>
                            <button className="mobile-crop-close" onClick={handleCropCancel}>×</button>
                        </div>
                        <div className="mobile-crop-content">
                            <AvatarEditor
                                width={250}
                                height={250}
                                imageWidth={250}
                                onCrop={onCrop}
                                onClose={onCloseCrop}
                                onBeforeFileLoad={onBeforeFileLoad}
                                src={selectedImageSrc || ""}
                                shadingColor={'rgba(0,0,0,0.5)'}
                                backgroundColor={'#0a0a0a'}
                                closeIconColor={'#FFB900'}
                                shadingOpacity={0.7}
                                cropRadius={125}
                                cropColor={'#FFB900'}
                                lineWidth={4}
                            />
                        </div>
                        <div className="mobile-crop-actions">
                            <button className="mobile-crop-cancel-btn" onClick={handleCropCancel}>
                                Cancel
                            </button>
                            <button
                                className="mobile-crop-save-btn"
                                onClick={handleCropSave}
                                disabled={!croppedImage}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileRegistration;
